/**
 * Chat API
 * Handles lecture Q&A and RAG interactions
 */
import apiClient, { getApiBaseUrl } from './client';

export interface SourceSegment {
  segment_id: number;
  lecture_name: string;
  shot_id: number;
  start_time: number;
  end_time: number;
  timestamp_str: string;
  score: number;
  keyframe_path?: string;
  transcript_text?: string;
}

export interface ChatMetrics {
  num_segments_retrieved: number;
  top_score: number;
  retrieval_time: number;
  generation_time: number;
  total_time: number;
  num_frames_processed?: number;
}

export interface AnswerResponse {
  question: string;
  answer: string;
  sources: SourceSegment[];
  session_id: string;
  conversation_context_used: number;
  metrics: ChatMetrics;
}

export interface QuestionRequest {
  question: string;
  lecture_name?: string;
  course_id?: string;
  session_id?: string;
}

export interface StreamCallbacks {
  onSources: (data: { sources: SourceSegment[]; metrics: any; session_id: string; conversation_context_used: number }) => void;
  onToken: (token: string) => void;
  onDone: (fullAnswer: string) => void;
  onError: (error: string) => void;
}

/**
 * Ask a question about lecture content
 */
export const askQuestion = async (data: QuestionRequest): Promise<AnswerResponse> => {
  const response = await apiClient.post<AnswerResponse>('/chat/ask', data);
  return response.data;
};

/**
 * Ask a question with streaming response (SSE)
 * Uses native fetch (not axios) because axios doesn't support browser streaming
 */
export const askQuestionStream = async (
  data: QuestionRequest,
  callbacks: StreamCallbacks
): Promise<void> => {
  const baseUrl = getApiBaseUrl();
  const token = localStorage.getItem('access_token');

  const response = await fetch(`${baseUrl}/chat/ask-stream`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data),
  });

  if (!response.ok) {
    const errorText = await response.text();
    throw new Error(errorText || `HTTP ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) throw new Error('No readable stream');

  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });

    // Parse SSE events from buffer
    const lines = buffer.split('\n');
    buffer = lines.pop() || ''; // keep incomplete line in buffer

    let currentEvent = '';
    for (const line of lines) {
      if (line.startsWith('event: ')) {
        currentEvent = line.slice(7).trim();
      } else if (line.startsWith('data: ')) {
        const jsonStr = line.slice(6);
        try {
          const parsed = JSON.parse(jsonStr);
          switch (currentEvent) {
            case 'sources':
              callbacks.onSources(parsed);
              break;
            case 'token':
              callbacks.onToken(parsed.token);
              break;
            case 'done':
              callbacks.onDone(parsed.full_answer);
              break;
            case 'error':
              callbacks.onError(parsed.error);
              break;
          }
        } catch {
          // skip malformed JSON
        }
        currentEvent = '';
      }
    }
  }
};

/**
 * Get conversation history for a session
 */
export const getConversationHistory = async (sessionId: string): Promise<any> => {
  const response = await apiClient.get(`/chat/history/${sessionId}`);
  return response.data;
};

/**
 * Clear conversation history
 */
export const clearConversation = async (sessionId: string): Promise<void> => {
  await apiClient.delete(`/chat/clear/${sessionId}`);
};
